
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ArrowRight, Loader2, List, Info, RotateCcw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ToolComparisonChallengeProps {
  userInitials: string;
  isChildComponent?: boolean;
}

interface ChatMessageData {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export const ToolComparisonChallenge = ({ userInitials, isChildComponent = false }: ToolComparisonChallengeProps) => {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessageData[]>([
    {
      role: 'assistant',
      content: "Today's challenge: Compare GPT-4 and Claude for analyzing customer feedback data. What are the strengths and weaknesses of each tool for this specific task? Provide your analysis.",
      timestamp: new Date().toISOString()
    }
  ]);
  const [activeTab, setActiveTab] = useState('exercise');

  const handleNewChallenge = () => {
    setIsLoading(true);
    
    const challenges = [
      "Compare ChatGPT and Bard for creating marketing content. What unique strengths does each tool offer?",
      "Compare Stable Diffusion and DALL-E for product visualization. Which would be better for your specific industry needs?",
      "Compare Jasper AI and Copy.ai for email campaign writing. What are the pros and cons of each?",
      "Compare Anthropic Claude and GPT-4 for data analysis tasks. How do they differ in reasoning capabilities?",
      "Compare Midjourney and Runway for creating concept visuals. Which offers better control for specific brand aesthetics?"
    ];
    
    const randomChallenge = challenges[Math.floor(Math.random() * challenges.length)];
    
    const newChallengeMessage: ChatMessageData = {
      role: 'assistant',
      content: `New challenge: ${randomChallenge}`,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages([newChallengeMessage]);
    setIsLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      toast({
        title: "Empty submission",
        description: "Please provide a comparison before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    // Add user message
    const userMessage: ChatMessageData = {
      role: 'user',
      content: prompt,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    
    try {
      // Get challenge description from the last assistant message
      // Instead of findLast, we'll use filter and then get the last item
      const assistantMessages = chatMessages.filter(msg => msg.role === 'assistant');
      const challengeDescription = assistantMessages.length > 0 
        ? assistantMessages[assistantMessages.length - 1].content 
        : '';
      
      const { data, error } = await supabase.functions.invoke('generate-practice-feedback', {
        body: {
          userPrompt: prompt,
          challengeDescription,
          previousMessages: chatMessages
        }
      });
      
      if (error) {
        throw new Error('Failed to get feedback.');
      }
      
      // Add assistant response
      const assistantMessage: ChatMessageData = {
        role: 'assistant',
        content: data.feedback,
        timestamp: new Date().toISOString()
      };
      
      setChatMessages((prev) => [...prev, assistantMessage]);
      setPrompt('');
    } catch (error) {
      console.error('Error submitting comparison:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit your comparison.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {!isChildComponent && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">AI Tool Comparison</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleNewChallenge}
            disabled={isLoading}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            New Challenge
          </Button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-grow flex flex-col">
        <TabsList className="w-full">
          <TabsTrigger value="exercise">Exercise</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
        </TabsList>
        
        <TabsContent value="exercise" className="flex-grow flex flex-col space-y-4 overflow-auto p-1">
          <div className="flex-grow overflow-auto">
            {chatMessages.map((message, index) => (
              <div 
                key={index} 
                className={`mb-4 p-3 rounded-lg ${
                  message.role === 'assistant' 
                    ? 'bg-muted' 
                    : 'bg-primary/10 ml-4'
                }`}
              >
                <div className="flex items-center mb-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 text-xs font-medium ${
                    message.role === 'assistant' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background text-foreground border'
                  }`}>
                    {message.role === 'assistant' ? 'AI' : userInitials}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="pl-8 whitespace-pre-wrap">{message.content}</div>
              </div>
            ))}
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-col space-y-3 mt-auto">
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Type your comparison analysis here..."
              className="min-h-[100px] resize-none"
              disabled={isLoading}
            />
            <Button type="submit" disabled={isLoading || !prompt.trim()}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Comparison
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>
        </TabsContent>
        
        <TabsContent value="resources" className="flex-grow flex flex-col p-4 space-y-4">
          <Card className="p-4 border-l-4 border-l-blue-500">
            <h4 className="flex items-center font-medium mb-2">
              <Info className="h-4 w-4 mr-2 text-blue-500" />
              Why Compare AI Tools?
            </h4>
            <p className="text-sm text-muted-foreground">
              Different AI tools have unique strengths, limitations, and specializations. 
              Understanding these differences helps you select the right tool for specific tasks 
              and combine tools effectively for optimal results.
            </p>
          </Card>
          
          <Card className="p-4 border-l-4 border-l-green-500">
            <h4 className="flex items-center font-medium mb-2">
              <List className="h-4 w-4 mr-2 text-green-500" />
              Comparison Framework
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Task suitability (what tasks the tool excels at)</li>
              <li>• Output quality and consistency</li>
              <li>• Ease of use and learning curve</li>
              <li>• Customization options</li>
              <li>• Integration capabilities</li>
              <li>• Cost structure and pricing</li>
              <li>• Limitations and constraints</li>
            </ul>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
